
import { useState, useRef, ClipboardEvent } from 'react';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';

interface MCQOption {
  id: string;
  text: string;
}

interface MCQQuestionProps {
  question: string;
  options: MCQOption[];
  onAnswer: (optionId: string) => void;
  selectedOption?: string;
  isDisabled?: boolean;
}

interface DescriptiveQuestionProps {
  question: string;
  onAnswer: (answer: string) => void;
  answer?: string;
  isDisabled?: boolean;
  preventPaste?: boolean;
  isAIDetected?: boolean;
}

export const MCQQuestion = ({ 
  question, 
  options, 
  onAnswer, 
  selectedOption,
  isDisabled = false
}: MCQQuestionProps) => {
  return (
    <div className="border rounded-xl p-5 mb-6 bg-white">
      <div className="flex items-start mb-4">
        <div className="flex-grow">
          <h3 className="font-medium text-apple-text mb-1">{question}</h3>
          <div className="text-sm text-apple-text-secondary flex items-center">
            <Clock size={14} className="mr-1" />
            Select the best answer
          </div>
        </div>
      </div>
      
      <div className="space-y-3">
        {options.map((option) => (
          <div 
            key={option.id}
            className={`border rounded-lg p-3.5 cursor-pointer transition-all ${
              selectedOption === option.id 
                ? 'border-apple-blue bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            } ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={() => !isDisabled && onAnswer(option.id)}
          >
            <div className="flex items-center">
              <div 
                className={`w-5 h-5 rounded-full border flex items-center justify-center mr-3 ${
                  selectedOption === option.id 
                    ? 'border-apple-blue bg-apple-blue text-white' 
                    : 'border-gray-300'
                }`}
              >
                {selectedOption === option.id && <CheckCircle size={12} />}
              </div>
              <span className="text-apple-text">{option.text}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DescriptiveQuestion = ({ 
  question, 
  onAnswer, 
  answer = '',
  isDisabled = false,
  preventPaste = false,
  isAIDetected = false
}: DescriptiveQuestionProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attemptedPaste, setAttemptedPaste] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onAnswer(e.target.value);
  };
  
  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (preventPaste) {
      e.preventDefault();
      setAttemptedPaste(true);
      // Show warning toast or alert the user
      console.log('Paste is not allowed for this question');
    }
  };
  
  return (
    <div className="border rounded-xl p-5 mb-6 bg-white">
      <div className="flex items-start mb-4">
        <div className="flex-grow">
          <h3 className="font-medium text-apple-text mb-1">{question}</h3>
          <div className="text-sm text-apple-text-secondary flex items-center">
            <Clock size={14} className="mr-1" />
            Provide a detailed answer
          </div>
        </div>
      </div>
      
      {preventPaste && (
        <div className="mb-4 text-sm text-amber-600 flex items-center">
          <AlertTriangle size={16} className="mr-2 flex-shrink-0" />
          <span>For assessment integrity, copy-paste is disabled for this question.</span>
        </div>
      )}
      
      {attemptedPaste && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm flex items-center">
          <AlertTriangle size={16} className="mr-2 flex-shrink-0" />
          <span>Paste action detected and blocked. Please type your answer manually.</span>
        </div>
      )}
      
      <Textarea
        ref={textareaRef}
        value={answer}
        onChange={handleChange}
        onPaste={handlePaste}
        className={`w-full min-h-[150px] p-4 rounded-lg transition-all ${
          isAIDetected ? 'border-2 border-amber-400 focus:border-amber-500 focus:ring-amber-500' : ''
        }`}
        placeholder="Write your answer here..."
        disabled={isDisabled}
      />
      
      {isAIDetected && (
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md text-amber-700 text-sm flex items-center">
          <AlertTriangle size={14} className="mr-2 flex-shrink-0" />
          <span>AI-generated content detected. Please provide your own answer in your own words.</span>
        </div>
      )}
      
      <div className="mt-3 text-xs text-apple-text-secondary text-right">
        {answer.length} characters
      </div>
    </div>
  );
};
